import { useCallback, useEffect, useRef, useState } from "react";
import { ZodError } from "zod/v4";
import { useApiConfig, type ApiConfig } from "@visioni/a3s-client-api/app";
import type { CameraAssetGroup } from "../../client-messages/vehicles";
import { isExportRangeAllowed } from "../../export/limits";
// Import-free like limits.ts, so it's safe to load eagerly; the workbook stays behind loadExport().
import { assertExportTimezone } from "../../export/timezone";
import type { VehicleFilters } from "../../types";
import { fetchAllVehicles } from "../fetch-all-vehicles";
import { loadExport } from "../load-export";

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * How an export ended. Failures resolve rather than throw, so the panel can
 * render every outcome from the result alone:
 * - `ok`: the workbook is ready; the caller triggers the download.
 * - `range-too-large`: the applied range breaks the export range rule; nothing was requested.
 * - `cancelled`: `cancel()` was called, or the screen unmounted, before it finished.
 * - `error`: a request failed or returned something unexpected; retrying may help.
 */
export type ExportResult =
  | { kind: "ok"; blob: Blob; fileName: string; rowCount: number; truncated: boolean }
  | { kind: "range-too-large" }
  | { kind: "cancelled" }
  | { kind: "error"; message: string };

export interface UseVehicleExportOptions {
  /** The applied (committed) filters, as the timeline queries them. */
  filters: VehicleFilters;
  selectedSiteId: string | null;
  sort?: "asc" | "desc" | undefined;
  /** IANA timezone the screen shows times in. */
  timezone: string;
  /** Cameras response, for camera nicknames. */
  cameras: CameraAssetGroup[];
}

export interface UseVehicleExport {
  /**
   * Fetches every matching sighting (up to the row cap) and builds the
   * workbook. While an export runs, calling it again returns the running
   * export rather than starting a second.
   */
  exportWorkbook: () => Promise<ExportResult>;
  isExporting: boolean;
  progress: { fetched: number };
  cancel: () => void;
}

/** Vehicle sightings export to `.xlsx`. Web only: on native every export ends in `error`. */
export function useVehicleExport({
  filters,
  selectedSiteId,
  sort,
  timezone,
  cameras,
}: UseVehicleExportOptions): UseVehicleExport {
  const apiConfig = useApiConfig();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState({ fetched: 0 });
  const controllerRef = useRef<AbortController | null>(null);
  const runningRef = useRef<Promise<ExportResult> | null>(null);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  // Cancelling detaches the run at once, so Download straight after Cancel
  // starts a fresh export. A detached run's late callbacks check that their
  // controller is still the current one before touching refs or state.
  const cancel = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    runningRef.current = null;
    setIsExporting(false);
  }, []);

  const exportWorkbook = useCallback((): Promise<ExportResult> => {
    if (runningRef.current) return runningRef.current;
    if (!isExportRangeAllowed(filters)) return Promise.resolve({ kind: "range-too-large" });

    const controller = new AbortController();
    const isCurrent = () => controllerRef.current === controller;
    controllerRef.current = controller;
    setIsExporting(true);
    setProgress({ fetched: 0 });

    const running = runExport({
      apiConfig,
      filters,
      selectedSiteId,
      sort,
      timezone,
      cameras,
      signal: controller.signal,
      onProgress: (fetched) => {
        if (isCurrent()) setProgress({ fetched });
      },
    }).finally(() => {
      if (!isCurrent()) return;
      controllerRef.current = null;
      runningRef.current = null;
      setIsExporting(false);
    });
    runningRef.current = running;
    return running;
  }, [apiConfig, filters, selectedSiteId, sort, timezone, cameras]);

  return { exportWorkbook, isExporting, progress, cancel };
}

interface RunExportInput extends UseVehicleExportOptions {
  apiConfig: ApiConfig;
  signal: AbortSignal;
  onProgress: (fetched: number) => void;
}

async function runExport(input: RunExportInput): Promise<ExportResult> {
  const { apiConfig, filters, selectedSiteId, sort, timezone, cameras, signal, onProgress } = input;
  try {
    if (!apiConfig.tenant) return { kind: "error", message: "Tenant not configured" };
    // Fail before paging through every sighting, not after: the workbook would reject it anyway.
    assertExportTimezone(timezone);
    const { vehicles, truncated } = await fetchAllVehicles({
      fetch: apiConfig.authenticatedFetch,
      tenant: apiConfig.tenant,
      filters,
      selectedSiteId,
      sort,
      signal,
      onProgress,
    });
    const exporter = await loadExport();
    signal.throwIfAborted();
    const rows = vehicles.map((vehicle) => exporter.toExportRow(vehicle, { timezone, cameras }));
    const workbook = await exporter.buildVehicleExportWorkbook(rows, { timezone, generatedAt: new Date(), truncated });
    signal.throwIfAborted();
    return {
      kind: "ok",
      blob: new Blob([workbook], { type: XLSX_TYPE }),
      fileName: exporter.exportFileName({ startDate: filters.startDate, endDate: filters.endDate, timezone }),
      rowCount: rows.length,
      truncated,
    };
  } catch (error) {
    if (signal.aborted) return { kind: "cancelled" };
    return { kind: "error", message: errorMessage(error) };
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof ZodError) return "The vehicle tracker sent an unexpected response.";
  return error instanceof Error ? error.message : "The export failed.";
}
